import { Button, cn } from "@au-schein-de/au-ui-library";
import { type FormEvent, useState } from "react";
import { GetVendorsFilteredDebugFormFields } from "./get-vendors-filtered-debug.form";
import {
	buildGetVendorsFilteredDebugInput,
	GET_VENDORS_FILTERED_DEBUG_INITIAL_FORM,
	type GetVendorsFilteredDebugFormState,
} from "./get-vendors-filtered-debug.utils";

/**
 * Temporary debug panel to exercise `public.getVendorsFiltered` from the PLP.
 */
export function GetVendorsFilteredDebugPanel() {
	const [isOpen, setIsOpen] = useState(false);
	const [form, setForm] = useState<GetVendorsFilteredDebugFormState>(
		GET_VENDORS_FILTERED_DEBUG_INITIAL_FORM,
	);
	const [isLoading, setIsLoading] = useState(false);
	const [responseText, setResponseText] = useState<string | null>(null);
	const [isCopied, setIsCopied] = useState(false);
	const update = <K extends keyof GetVendorsFilteredDebugFormState>(
		key: K,
		value: GetVendorsFilteredDebugFormState[K],
	) => {
		setForm((prev) => ({ ...prev, [key]: value }));
	};
	const onSubmit = async (event: FormEvent) => {
		event.preventDefault();
		setIsLoading(true);
		try {
			const response = await fetch("/api/vendors-filtered", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(buildGetVendorsFilteredDebugInput(form)),
			});
			const payload = await response.json();
			setResponseText(JSON.stringify(payload, null, 2));
			setIsCopied(false);
		} catch (error) {
			setResponseText(
				JSON.stringify(
					{ error: error instanceof Error ? error.message : "Request failed" },
					null,
					2,
				),
			);
			setIsCopied(false);
		} finally {
			setIsLoading(false);
		}
	};
	const copyResponse = async () => {
		if (responseText == null) {
			return;
		}
		await navigator.clipboard.writeText(responseText);
		setIsCopied(true);
	};
	return (
		<div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
			{isOpen && (
				<div
					className={cn(
						"flex max-h-[80vh] w-[min(100vw-2rem,28rem)] flex-col gap-3 overflow-auto",
						"rounded-lg border border-gray-300 bg-white p-4 shadow-lg",
					)}
					data-testid="get-vendors-filtered-debug-panel"
				>
					<div className="flex items-center justify-between gap-2">
						<h2 className="text-body-md">getVendorsFiltered</h2>
						<Button
							type="button"
							variant="ghost"
							size="md"
							onClick={() => setIsOpen(false)}
						>
							Close
						</Button>
					</div>
					<form className="flex flex-col gap-3" onSubmit={onSubmit}>
						<GetVendorsFilteredDebugFormFields form={form} update={update} />
						<Button type="submit" disabled={isLoading}>
							{isLoading ? "Loading…" : "Call getVendorsFiltered"}
						</Button>
					</form>
					{responseText != null && (
						<div className="flex flex-col gap-2">
							<div className="flex justify-end">
								<Button
									type="button"
									variant="ghost"
									size="md"
									onClick={copyResponse}
									data-testid="get-vendors-filtered-debug-copy"
								>
									{isCopied ? "Copied" : "Copy"}
								</Button>
							</div>
							<pre
								className="max-h-64 overflow-auto rounded bg-gray-100 p-2 text-body-sm whitespace-pre-wrap break-words"
								data-testid="get-vendors-filtered-debug-response"
							>
								{responseText}
							</pre>
						</div>
					)}
				</div>
			)}
			<Button
				type="button"
				onClick={() => setIsOpen((open) => !open)}
				data-testid="get-vendors-filtered-debug-toggle"
			>
				{isOpen ? "Hide vendors filtered" : "getVendorsFiltered"}
			</Button>
		</div>
	);
}
